import {all, call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';

import request from 'utils/request';

import {makeSelectUserToken} from 'reducers/selectors/userSelectors';

import {
  CREATE_COMPANY,
  CREATE_USER,
  createCompanyFailed,
  createCompanySuccess,
  createUserFailed,
  createUserSuccess,
  DELETE_COMPANY,
  deleteCompanyFailed,
  deleteCompanySuccess,
  FETCH_ABOUT,
  FETCH_COMPANIES,
  FETCH_COMPANY,
  FETCH_COUNTRIES,
  FETCH_GROUPS,
  FETCH_PAGES,
  FETCH_USERS,
  fetchAboutFailed,
  fetchAboutSuccess,
  fetchCompaniesFailed,
  fetchCompaniesSuccess,
  fetchCompanies as fetchCompaniesAction,
  fetchCountriesFailed,
  fetchCountriesSuccess,
  fetchGroupsFailed,
  fetchGroupsSuccess,
  fetchPagesFailed,
  fetchPagesSuccess,
  fetchUsersFailed,
  fetchUsersSuccess,
  getCompanyByIdFailed,
  getCompanyByIdSuccess,
  getCompanyById as getCompanyByIdAction,
  LINK_LOGO,
  linkLogoFailed,
  linkLogoSuccess,
  SAVE_COMPANY,
  saveCompanyFailed,
  saveCompanySuccess,
  UPDATE_COMPANY,
  UPDATE_PARTNER,
  updateCompanyFailed,
  updateCompanySuccess,
} from 'actions/companyActions';
import {getQueryString} from 'utils/queryString';
import {getCompanyProfileState, serializeCompanyProfile} from 'utils/companyProfile';

function* fetchCompanies(action) {
  const params = getQueryString(action.filters);
  const requestUrl = `company?${params}`;
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(
      fetchCompaniesSuccess({
        companies: response.companies,
        statuses: response.statuses,
        total: response.total,
        pageNumberBack: response.pageNumberBack,
      })
    );
  } catch (err) {
    yield put(fetchCompaniesFailed(err));
  }
}

function* fetchGroups() {
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, `group`, authToken, {});
    yield put(fetchGroupsSuccess(response.groups));
  } catch (err) {
    yield put(fetchGroupsFailed(err));
  }
}

export function* saveCompany(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = action.review
    ? `company/${action.details._id}?review=true`
    : `company/${action.details._id}`;

  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(serializeCompanyProfile(action.details)),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(saveCompanySuccess(response.company, action.details.action, getCompanyProfileState(response)));
  } catch (err) {
    console.log('run here with an error', err);
    yield put(saveCompanyFailed(err));
  }
}

export function* updateCompany(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = action.review
    ? `company/${action.details._id}?review=true`
    : `company/${action.details._id}`;

  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(serializeCompanyProfile(action.details)),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateCompanySuccess(response.company, action.details.action, getCompanyProfileState(response)));
    if (action.filters) yield put(fetchCompaniesAction(action.filters, true));
  } catch (err) {
    yield put(updateCompanyFailed(err));
    const activeCompany = yield select((state) => state.company?.company);
    if (activeCompany?._id === action.details._id) yield put(getCompanyByIdAction(action.details._id, true));
  }
}

function* updatePartner(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `company/${action.details._id}/partner`;
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(action.details),
  };
  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateCompanySuccess(response.company, ''));
  } catch (err) {
    yield put(updateCompanyFailed(err));
  }
}

export function* deleteCompany(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = `company/${action.companyId}`;

  const requestOptions = {
    method: 'DELETE',
  };

  try {
    yield call(request, requestUrl, authToken, requestOptions);
    yield put(deleteCompanySuccess({_id: action.companyId}));
    if (action.filters) yield put(fetchCompaniesAction(action.filters, true));
  } catch (err) {
    yield put(deleteCompanyFailed(err));
  }
}

export function* createCompany(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = `company`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(serializeCompanyProfile(action.details)),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    const profileResponse = response.company ? response : {company: response};
    yield put(createCompanySuccess(profileResponse.company, getCompanyProfileState(profileResponse)));
  } catch (err) {
    yield put(createCompanyFailed(err));
  }
}

export function* getCompanyById(action) {
  const authToken = yield select(makeSelectUserToken());
  const url = action.status
    ? `company/${action.id}?status=true`
    : `company/${action.id}`;

  try {
    const response = yield call(request, url, authToken, {});
    const {posts, pageFolders, publishedAbout} = response;
    const company = {...response.company};
    company.bothPublished =
      company.status === 'PUBLISHED' &&
      publishedAbout &&
      publishedAbout.status === 'PUBLISHED';

    yield put(getCompanyByIdSuccess(company, posts, pageFolders, getCompanyProfileState(response)));
  } catch (err) {
    yield put(getCompanyByIdFailed(err));
  }
}

function* fetchAbout(action) {
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(
      request,
      `post/${action.id}/about`,
      authToken,
      {}
    );
    yield put(fetchAboutSuccess(response.post));
  } catch (err) {
    yield put(fetchAboutFailed(err));
  }
}

function* linkLogo(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = `company/${action.logoId}/linkLogo`;

  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify({companyId: action.companyId}),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(linkLogoSuccess(response.company));
  } catch (err) {
    yield put(linkLogoFailed(err));
  }
}

function* fetchUsers(action) {
  const requestUrl = `user/filterByParams?companyId=${action.companyId}&pageSize=200&pageNumber=0&isDeleted=false`;
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchUsersSuccess(response.user));
  } catch (err) {
    yield put(fetchUsersFailed(err));
  }
}

function* createUser(action) {
  const authToken = yield select(makeSelectUserToken());

  const requestUrl = `company/${action.companyId}/register`;

  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.userDetails),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(createUserSuccess(response.user));
  } catch (err) {
    yield put(createUserFailed(err));
  }
}

function* fetchPages(action) {
  const requestUrl = `pages/companyPages/${action.companyId}`;
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchPagesSuccess(response.pageFolders));
  } catch (err) {
    yield put(fetchPagesFailed(err));
  }
}

function* fetchCountries() {
  const authToken = yield select(makeSelectUserToken());
  try {
    const requestUrl = `company/list/countries`;
    const response = yield call(request, requestUrl, authToken, {});
    yield put(fetchCountriesSuccess(response));
  } catch (err) {
    yield put(fetchCountriesFailed(err));
  }
}

export default function* companySagas() {
  yield all([
    takeLatest(FETCH_GROUPS, fetchGroups),
    takeLatest(FETCH_COMPANIES, fetchCompanies),
    takeLatest(CREATE_COMPANY, createCompany),
    takeLatest(SAVE_COMPANY, saveCompany),
    takeLatest((action) => action.type === UPDATE_COMPANY && !action.filters, updateCompany),
    takeEvery((action) => action.type === UPDATE_COMPANY && Boolean(action.filters), updateCompany),
    takeLatest(UPDATE_PARTNER, updatePartner),
    takeLatest(FETCH_COMPANY, getCompanyById),
    takeLatest(FETCH_ABOUT, fetchAbout),
    takeLatest(LINK_LOGO, linkLogo),
    takeEvery(DELETE_COMPANY, deleteCompany),
    takeLatest(CREATE_USER, createUser),
    takeLatest(FETCH_USERS, fetchUsers),
    takeLatest(FETCH_PAGES, fetchPages),
    takeLatest(FETCH_COUNTRIES, fetchCountries),
  ]);
}
